import React, { useState, useEffect, useRef } from 'react'
import { Form, Select, Button, Tag, Input, Modal, Row, Col, Card, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPills, faAppleAlt, faAllergies } from '@fortawesome/free-solid-svg-icons'
import uniqBy from 'lodash'
import '../style/DesktopAllergies.css'
import {
  ALLERGY_CATEGORIES,
  COMMON_ALLERGIES,
  COMMON_FOOD_ALLERGIES,
  COMMON_GENERAL_ALLERGIES
} from '../../utils/constants'
import useAllergies from '../../hooks/useAllergies'
import DesktopMedicalHistoryNav from './Nav'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopAllergies = ({ clientForm, loading, setFacet, setSaving }) => {
  const {
    addFoodAllergy,
    addGeneralAllergy,
    additionalFoodAllergies,
    additionalGeneralAllergies,
    additionalMedications,
    debouncedNappiFilter,
    handleFoodOptionClick,
    handleGeneralOptionClick,
    handleOptionClick,
    handleSave,
    isModalVisible,
    nappiData,
    nappiLoading,
    removeFoodAllergy,
    removeGeneralAllergy,
    selectedAllergies,
    selectedCategory,
    selectedFoodAllergies,
    selectedGeneralAllergies,
    setAdditionalFoodAllergies,
    setAdditionalGeneralAllergies,
    setAdditionalMedications,
    setIsModalVisible,
    setNappiData,
    setSelectedAllergies,
    setSelectedFoodAllergies,
    setSelectedGeneralAllergies,
    setSelectedCategory
  } = useAllergies({ clientForm, setSaving, setFacet })

  const [newFoodAllergy, setNewFoodAllergy] = useState('')
  const [newGeneralAllergy, setNewGeneralAllergy] = useState('')
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when entering a category - with delay to ensure state is loaded
  useEffect(() => {
    if (selectedCategory) {
      const timer = setTimeout(() => {
        if (!initialState.current) {
          initialState.current = {
            selectedAllergies: [...selectedAllergies],
            selectedFoodAllergies: [...selectedFoodAllergies],
            selectedGeneralAllergies: [...selectedGeneralAllergies],
            additionalMedications: [...additionalMedications],
            additionalFoodAllergies: [...additionalFoodAllergies],
            additionalGeneralAllergies: [...additionalGeneralAllergies]
          }
          console.log('Initial state captured:', initialState.current)
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [selectedCategory])

  // Clear initial state when leaving category
  useEffect(() => {
    if (!selectedCategory) {
      initialState.current = null
    }
  }, [selectedCategory])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current || !selectedCategory) return false

    const current = {
      selectedAllergies,
      selectedFoodAllergies,
      selectedGeneralAllergies,
      additionalMedications,
      additionalFoodAllergies,
      additionalGeneralAllergies
    }

    const hasChanges = JSON.stringify(current) !== JSON.stringify(initialState.current)
    console.log('Change detection:', {
      hasChanges,
      initial: initialState.current,
      current
    })

    return hasChanges
  }

  const addMedication = (name) => {
    if (name && !additionalMedications.includes(name)) {
      setAdditionalMedications([...additionalMedications, name])
    }
  }

  const removeMedication = (name) => {
    setAdditionalMedications(additionalMedications.filter((med) => med !== name))
  }

  const handleAddFoodAllergy = () => {
    if (newFoodAllergy.trim()) {
      addFoodAllergy(newFoodAllergy.trim())
      setNewFoodAllergy('')
      setIsModalVisible(false)
    }
  }

  const handleAddGeneralAllergy = () => {
    if (newGeneralAllergy.trim()) {
      addGeneralAllergy(newGeneralAllergy.trim())
      setNewGeneralAllergy('')
      setIsModalVisible(false)
    }
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = {
      selectedAllergies: [...selectedAllergies],
      selectedFoodAllergies: [...selectedFoodAllergies],
      selectedGeneralAllergies: [...selectedGeneralAllergies],
      additionalMedications: [...additionalMedications],
      additionalFoodAllergies: [...additionalFoodAllergies],
      additionalGeneralAllergies: [...additionalGeneralAllergies]
    }
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSave that also updates initial state
  const handleSaveWithStateUpdate = async (navigateToOverview) => {
    try {
      await handleSave(navigateToOverview)
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSave
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSave(false)
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSave
    }
  }

  // Handle navigation without saving - restore initial state
  const handleNavigateWithoutSaving = () => {
    if (initialState.current) {
      // Restore all state to initial values
      setSelectedAllergies(initialState.current.selectedAllergies)
      setSelectedFoodAllergies(initialState.current.selectedFoodAllergies)
      setSelectedGeneralAllergies(initialState.current.selectedGeneralAllergies)
      setAdditionalMedications(initialState.current.additionalMedications)
      setAdditionalFoodAllergies(initialState.current.additionalFoodAllergies)
      setAdditionalGeneralAllergies(initialState.current.additionalGeneralAllergies)

      // Also update form values to reflect the restored state
      const currentValues = clientForm.getFieldsValue()
      clientForm.setFieldsValue({
        allergies: {
          ...currentValues.allergies,
          meds: initialState.current.selectedAllergies,
          food: initialState.current.selectedFoodAllergies,
          general: initialState.current.selectedGeneralAllergies
        }
      })
    }

    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  return (
    <div className='desktop-allergies-container'>
      {!selectedCategory ? (
        <>
          <DesktopSectionHeader
            icon={faAllergies}
            title='Allergies'
            subtitle='Select a category to manage your allergies'
            gradient='linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)'
          />
          <div className='desktop-allergies-content'>
            <Row gutter={[16, 16]}>
              {ALLERGY_CATEGORIES.map((category) => (
                <Col xs={24} sm={12} md={8} key={category.id}>
                  <Card
                    className='desktop-category-card'
                    hoverable
                    onClick={() => setSelectedCategory(category.id)}
                    style={{ '--category-color': category.color }}
                  >
                    <div className='desktop-category-content'>
                      <div className='desktop-category-icon'>
                        <FontAwesomeIcon icon={category.icon} />
                      </div>
                      <h3>{category.label}</h3>
                      <p>{category.description}</p>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </>
      ) : selectedCategory === 'medications' ? (
        <>
          <DesktopSectionHeader
            icon={faPills}
            title='Medications'
            subtitle="Select any medications you're allergic to"
            gradient='linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)'
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${selectedAllergies.includes(allergy.id) ? 'selected' : ''}`}
                      role='checkbox'
                      onClick={() => handleOptionClick(allergy)}
                      aria-checked={selectedAllergies.includes(allergy.id)}
                      tabIndex={0}
                      style={{
                        '--category-color': '255, 107, 107'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faPills} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'meds', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <div className='desktop-add-option-container' style={{ '--category-color': '255, 107, 107' }}>
                    <Button
                      type='dashed'
                      className='desktop-add-option-btn'
                      onClick={() => setIsModalVisible(true)}
                      block
                    >
                      <FontAwesomeIcon icon={faPills} style={{ marginRight: 8 }} />
                      Add More
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>

            <div className='desktop-additional-medications' style={{ '--category-color': '255, 107, 107' }}>
              {additionalMedications.length > 0 && (
                <>
                  <div className='desktop-additional-header'>
                    <h4>Additional Medications</h4>
                  </div>
                  <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                    {additionalMedications.map((medication, index) => (
                      <Col key={index}>
                        <Tag
                          closable
                          onClose={() => removeMedication(medication)}
                          className='desktop-allergy-tag'
                          style={{ '--category-color': '255, 107, 107' }}
                        >
                          {medication.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Tag>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </div>

            <Modal
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FontAwesomeIcon icon={faPills} style={{ color: '#10b981' }} />
                  <span>Search and Add Medications</span>
                </div>
              }
              open={isModalVisible}
              onCancel={() => {
                setIsModalVisible(false)
                setNappiData([])
              }}
              footer={null}
              className='desktop-allergies-modal'
            >
              <Select
                showSearch
                placeholder='Search medications...'
                loading={nappiLoading}
                onSearch={debouncedNappiFilter}
                onSelect={(value) => {
                  addMedication(value)
                  setNappiData([])
                  setIsModalVisible(false)
                }}
                options={uniqBy(nappiData, 'name').map((i) => ({
                  value: i.name,
                  label: i.name
                }))}
                className='desktop-allergy-search'
                notFoundContent={
                  nappiLoading ? (
                    <div className='search-loading'>
                      <FontAwesomeIcon icon={faPills} spin />
                      <span>Searching...</span>
                    </div>
                  ) : null
                }
              />
            </Modal>
          </div>
        </>
      ) : selectedCategory === 'food' ? (
        <>
          <DesktopSectionHeader
            icon={faAppleAlt}
            title='Food Allergies'
            subtitle="Select any foods you're allergic to"
            gradient='linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)'
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_FOOD_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${
                        selectedFoodAllergies.includes(allergy.id) ? 'selected' : ''
                      }`}
                      role='checkbox'
                      onClick={() => handleFoodOptionClick(allergy)}
                      aria-checked={selectedFoodAllergies.includes(allergy.id)}
                      tabIndex={0}
                      style={{
                        '--category-color': '81, 207, 102'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleFoodOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faAppleAlt} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'food', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <div className='desktop-add-option-container' style={{ '--category-color': '81, 207, 102' }}>
                    <Button
                      type='dashed'
                      className='desktop-add-option-btn'
                      onClick={() => setIsModalVisible(true)}
                      block
                    >
                      <FontAwesomeIcon icon={faAppleAlt} style={{ marginRight: 8 }} />
                      Add More
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>

            <div className='desktop-additional-allergies' style={{ '--category-color': '81, 207, 102' }}>
              {additionalFoodAllergies.length > 0 && (
                <>
                  <div className='desktop-additional-header'>
                    <h4>Additional Food Allergies</h4>
                  </div>
                  <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                    {additionalFoodAllergies.map((food, index) => (
                      <Col key={index}>
                        <Tag
                          closable
                          onClose={() => removeFoodAllergy(food)}
                          className='desktop-allergy-tag'
                          style={{ '--category-color': '81, 207, 102' }}
                        >
                          {food.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Tag>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </div>

            <Modal
              title='Add Food Allergies'
              open={isModalVisible}
              onOk={handleAddFoodAllergy}
              onCancel={() => setIsModalVisible(false)}
              className='desktop-allergies-modal'
            >
              <Input
                placeholder='Type food allergy and press Enter'
                value={newFoodAllergy}
                onChange={(e) => setNewFoodAllergy(e.target.value)}
                onPressEnter={handleAddFoodAllergy}
                className='desktop-allergy-input'
              />
            </Modal>
          </div>
        </>
      ) : selectedCategory === 'general' ? (
        <>
          <DesktopSectionHeader
            icon={faAllergies}
            title='General Allergies'
            subtitle='Select any environmental or other allergies'
            gradient='linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)'
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_GENERAL_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${
                        selectedGeneralAllergies.includes(allergy.id) ? 'selected' : ''
                      }`}
                      role='checkbox'
                      onClick={() => handleGeneralOptionClick(allergy)}
                      aria-checked={selectedGeneralAllergies.includes(allergy.id)}
                      tabIndex={0}
                      style={{
                        '--category-color': '51, 154, 240'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleGeneralOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faAllergies} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'general', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <div className='desktop-add-option-container' style={{ '--category-color': '51, 154, 240' }}>
                    <Button
                      type='dashed'
                      className='desktop-add-option-btn'
                      onClick={() => setIsModalVisible(true)}
                      block
                    >
                      <FontAwesomeIcon icon={faAllergies} style={{ marginRight: 8 }} />
                      Add More
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>

            <div className='desktop-additional-allergies' style={{ '--category-color': '51, 154, 240' }}>
              {additionalGeneralAllergies.length > 0 && (
                <>
                  <div className='desktop-additional-header'>
                    <h4>Additional General Allergies</h4>
                  </div>
                  <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                    {additionalGeneralAllergies.map((allergy, index) => (
                      <Col key={index}>
                        <Tag
                          closable
                          onClose={() => removeGeneralAllergy(allergy)}
                          className='desktop-allergy-tag'
                          style={{ '--category-color': '51, 154, 240' }}
                        >
                          {allergy.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Tag>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </div>

            <Modal
              title='Add General Allergies'
              open={isModalVisible}
              onOk={handleAddGeneralAllergy}
              onCancel={() => setIsModalVisible(false)}
              className='desktop-allergies-modal'
            >
              <Input
                placeholder='Type general allergy and press Enter'
                value={newGeneralAllergy}
                onChange={(e) => setNewGeneralAllergy(e.target.value)}
                onPressEnter={handleAddGeneralAllergy}
                className='desktop-allergy-input'
              />
            </Modal>
          </div>
        </>
      ) : null}

      <DesktopMedicalHistoryNav
        loading={loading}
        prevFunction={() => handleNavigation(() => (selectedCategory ? setSelectedCategory(null) : setFacet(null)))}
        nextFunction={() => handleSaveWithStateUpdate(true)}
        prevText={selectedCategory ? 'Go Back' : 'Back to Overview'}
        nextText='Save Allergies'
      />

      {/* Unsaved Changes Confirmation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faAllergies} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your allergy information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button
              type='primary'
              className='desktop-save-changes-btn'
              onClick={handleSaveAndNavigate}
              loading={loading}
            >
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DesktopAllergies
